import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Grid,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Refresh as RefreshIcon,
  CurrencyExchange as RefundIcon,
  Search as SearchIcon,
  Download as DownloadIcon,
  CheckCircle as MarkPaidIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout from '../../../layouts/AdminLayout';
import unifiedApi from '../../../utils/unifiedApi';
import { useIsAdmin } from '../../../contexts/PermissionsContext';

const STATUS_LABELS = {
  Pending: 'Pending',
  Paid: 'Paid',
  Expired: 'Expired',
  Cancelled: 'Cancelled',
  Refunded: 'Refunded',
};

const STATUS_COLORS = {
  Pending: 'warning',
  Paid: 'success',
  Expired: 'default',
  Cancelled: 'error',
  Refunded: 'info',
};

const EventRegistrationDashboardPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [inventory, setInventory] = useState(null);
  const [eventName, setEventName] = useState('');

  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [existingPlayerFilter, setExistingPlayerFilter] = useState('');

  // Detail dialog
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  // Refund dialog
  const [refundTarget, setRefundTarget] = useState(null);
  const [refundLoading, setRefundLoading] = useState(false);

  // Mark-as-paid dialog
  const [markPaidTarget, setMarkPaidTarget] = useState(null);
  const [markPaidLoading, setMarkPaidLoading] = useState(false);

  // Delete dialog
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // Search
  const [searchText, setSearchText] = useState('');

  // Export
  const [exporting, setExporting] = useState(false);

  // Sort state
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState('desc');

  const handleSort = (column) => {
    const isAsc = orderBy === column && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(column);
  };

  const filteredRegistrations = useMemo(() => {
    if (!searchText.trim()) return registrations;
    const term = searchText.toLowerCase();
    return registrations.filter(reg => {
      const name = `${reg.firstName} ${reg.lastName}`.toLowerCase();
      const email = (reg.email || '').toLowerCase();
      const status = (STATUS_LABELS[reg.status] || '').toLowerCase();
      return name.includes(term) || email.includes(term) || status.includes(term);
    });
  }, [registrations, searchText]);

  const sortedRegistrations = useMemo(() => {
    const comparator = (a, b) => {
      let aVal, bVal;
      if (orderBy === 'name') {
        aVal = `${a.firstName} ${a.lastName}`.toLowerCase();
        bVal = `${b.firstName} ${b.lastName}`.toLowerCase();
      } else if (orderBy === 'status') {
        aVal = (STATUS_LABELS[a.status] || '').toLowerCase();
        bVal = (STATUS_LABELS[b.status] || '').toLowerCase();
      } else {
        aVal = a[orderBy] ?? '';
        bVal = b[orderBy] ?? '';
      }
      if (aVal < bVal) return order === 'asc' ? -1 : 1;
      if (aVal > bVal) return order === 'asc' ? 1 : -1;
      return 0;
    };
    return [...filteredRegistrations].sort(comparator);
  }, [filteredRegistrations, orderBy, order]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {};
      if (statusFilter) filters.status = statusFilter;
      if (existingPlayerFilter) filters.isExistingPlayer = existingPlayerFilter;

      const [regs, inv, event] = await Promise.all([
        unifiedApi.getEventRegistrations(eventId, filters),
        unifiedApi.getEventRegistrationInventory(eventId),
        unifiedApi.getEvent(eventId),
      ]);
      setRegistrations(regs || []);
      setInventory(inv);
      setEventName(event?.name || '');
    } catch {
      setError('Failed to load registration data.');
    } finally {
      setLoading(false);
    }
  }, [eventId, statusFilter, existingPlayerFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleViewDetails = async (regId) => {
    setDetailDialogOpen(true);
    setDetailLoading(true);
    setSelectedRegistration(null);
    try {
      const detail = await unifiedApi.getEventRegistration(eventId, regId);
      if (!detail) {
        setError('Registration not found — it may have been deleted.');
        setDetailDialogOpen(false);
        return;
      }
      setSelectedRegistration(detail);
    } catch {
      setError('Failed to load registration details.');
      setDetailDialogOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString();
  };

  const formatPrice = (cents) => {
    return (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  const handleRefund = async () => {
    if (!refundTarget) return;
    setRefundLoading(true);
    try {
      await unifiedApi.refundEventRegistration(eventId, refundTarget.id);
      setRefundTarget(null);
      fetchData();
    } catch {
      setError('Failed to process refund.');
    } finally {
      setRefundLoading(false);
    }
  };

  const handleMarkPaid = async () => {
    if (!markPaidTarget) return;
    setMarkPaidLoading(true);
    try {
      await unifiedApi.markEventRegistrationPaid(eventId, markPaidTarget.id);
      setMarkPaidTarget(null);
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to mark registration as paid.');
    } finally {
      setMarkPaidLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await unifiedApi.deleteEventRegistration(eventId, deleteTarget.id);
      setDeleteTarget(null);
      fetchData();
    } catch (err) {
      setError(err?.message || 'Failed to delete registration.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await unifiedApi.exportEventRegistrations(eventId);
      if (blob && blob.size > 0) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `event-${eventId}-registrations.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        setError('No data to export.');
      }
    } catch {
      setError('Failed to export registrations.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <AdminLayout>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/admin/events')}
          sx={{ mb: 2 }}
        >
          Back to Events
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4">
            {eventName ? `${eventName} — Registrations` : 'Event Registrations'}
          </Typography>
          <Stack direction="row" spacing={1}>
            <Button startIcon={<DownloadIcon />} onClick={handleExport} disabled={exporting || loading}>
              {exporting ? 'Exporting…' : 'Export Participants'}
            </Button>
            <Button startIcon={<RefreshIcon />} onClick={fetchData} disabled={loading}>
              Refresh
            </Button>
          </Stack>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Inventory Summary */}
      {inventory && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>Inventory Summary</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="h4">{inventory.totalSlots}</Typography>
                <Typography variant="body2" color="text.secondary">Total Slots</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="h4">{inventory.availableSlots}</Typography>
                <Typography variant="body2" color="text.secondary">Available</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                <Typography variant="h4" color="success.main">{inventory.paidRegistrations}</Typography>
                <Typography variant="body2" color="text.secondary">Paid</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.50', borderRadius: 1 }}>
                <Typography variant="h4" color="warning.main">{inventory.pendingRegistrations}</Typography>
                <Typography variant="body2" color="text.secondary">Pending</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                <Typography variant="h4" color="info.main">{inventory.refundedRegistrations || 0}</Typography>
                <Typography variant="body2" color="text.secondary">Refunded</Typography>
              </Box>
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                <Typography variant="h4" color="info.main">{formatPrice(inventory.totalPaymentsInCents || 0)}</Typography>
                <Typography variant="body2" color="text.secondary">Collected</Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            placeholder="Search registrations..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ minWidth: 220 }}
            inputProps={{ 'aria-label': 'Search registrations' }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} label="Status">
              <MenuItem value="">All</MenuItem>
              {Object.entries(STATUS_LABELS).map(([val, label]) => (
                <MenuItem key={val} value={val}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Existing Player</InputLabel>
            <Select value={existingPlayerFilter} onChange={(e) => setExistingPlayerFilter(e.target.value)} label="Existing Player">
              <MenuItem value="">All</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {/* Registrations Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sortDirection={orderBy === 'id' ? order : false}>
                    <TableSortLabel active={orderBy === 'id'} direction={orderBy === 'id' ? order : 'asc'} onClick={() => handleSort('id')}>ID</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'name' ? order : false}>
                    <TableSortLabel active={orderBy === 'name'} direction={orderBy === 'name' ? order : 'asc'} onClick={() => handleSort('name')}>Name</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'email' ? order : false}>
                    <TableSortLabel active={orderBy === 'email'} direction={orderBy === 'email' ? order : 'asc'} onClick={() => handleSort('email')}>Email</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'status' ? order : false}>
                    <TableSortLabel active={orderBy === 'status'} direction={orderBy === 'status' ? order : 'asc'} onClick={() => handleSort('status')}>Status</TableSortLabel>
                  </TableCell>
                  <TableCell sortDirection={orderBy === 'createdAt' ? order : false}>
                    <TableSortLabel active={orderBy === 'createdAt'} direction={orderBy === 'createdAt' ? order : 'asc'} onClick={() => handleSort('createdAt')}>Registered</TableSortLabel>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRegistrations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        No registrations found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedRegistrations.map(reg => (
                    <TableRow key={reg.id} hover>
                      <TableCell>{reg.id}</TableCell>
                      <TableCell>
                        {reg.userTenantId ? (
                          <Link to={`/player/${reg.userTenantId}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                            <Typography variant="body2" sx={{ color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}>
                              {reg.firstName} {reg.lastName}
                            </Typography>
                          </Link>
                        ) : (
                          `${reg.firstName} ${reg.lastName}`
                        )}
                      </TableCell>
                      <TableCell>{reg.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={STATUS_LABELS[reg.status]}
                          size="small"
                          color={STATUS_COLORS[reg.status]}
                          clickable={false}
                          onClick={() => {}}
                        />
                      </TableCell>
                      <TableCell>{formatDate(reg.createdAt)}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Button size="small" onClick={() => handleViewDetails(reg.id)}>
                            View
                          </Button>
                          {isAdmin && reg.status === 'Paid' && (
                            <Button
                              size="small"
                              color="info"
                              startIcon={<RefundIcon />}
                              onClick={() => setRefundTarget(reg)}
                            >
                              Refund
                            </Button>
                          )}
                          {isAdmin && reg.status !== 'Paid' && (
                            <Button
                              size="small"
                              color="success"
                              startIcon={<MarkPaidIcon />}
                              onClick={() => setMarkPaidTarget(reg)}
                            >
                              Mark Paid
                            </Button>
                          )}
                          {isAdmin && reg.status !== 'Paid' && (
                            <Button
                              size="small"
                              color="error"
                              startIcon={<DeleteIcon />}
                              onClick={() => setDeleteTarget(reg)}
                            >
                              Delete
                            </Button>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Registration Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => { setDetailDialogOpen(false); setSelectedRegistration(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedRegistration
            ? `Registration Details — ${selectedRegistration.firstName} ${selectedRegistration.lastName}`
            : 'Registration Details'}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : selectedRegistration ? (
            <Stack spacing={1.5}>
              <DetailRow label="Registration ID" value={selectedRegistration.id} />
              <DetailRow label="Email" value={selectedRegistration.email} />
              <DetailRow label="Phone" value={selectedRegistration.phone} />
              <DetailRow label="Status" value={STATUS_LABELS[selectedRegistration.status]} />
              <DetailRow label="Birthday" value={selectedRegistration.birthday ? new Date(selectedRegistration.birthday).toLocaleDateString() : '—'} />
              <DetailRow label="Postal Code" value={selectedRegistration.postalCode} />
              <DetailRow label="Existing Player" value={selectedRegistration.userTenantId ? 'Yes' : 'No'} />
              <DetailRow label="Waiver Accepted" value={formatDate(selectedRegistration.waiverAcceptedAt)} />
              {selectedRegistration.customFieldValue && (
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {selectedRegistration.customFieldLabel || 'Custom Field'}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {selectedRegistration.customFieldValue}
                  </Typography>
                </Box>
              )}
              <DetailRow label="Expires At" value={formatDate(selectedRegistration.expiresAt)} />
              <DetailRow label="Registered At" value={formatDate(selectedRegistration.createdAt)} />
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDetailDialogOpen(false); setSelectedRegistration(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Refund Confirmation Dialog */}
      <Dialog
        open={!!refundTarget}
        onClose={() => setRefundTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        {refundTarget && (
          <>
            <DialogTitle>Mark Registration as Refunded</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Are you sure you want to mark the registration for{' '}
                <strong>{refundTarget.firstName} {refundTarget.lastName}</strong> ({refundTarget.email}) as refunded?
                This will free their slot.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRefundTarget(null)}>
                Cancel
              </Button>
              <Button
                onClick={handleRefund}
                color="info"
                variant="contained"
                disabled={refundLoading}
                startIcon={refundLoading ? <CircularProgress size={16} /> : <RefundIcon />}
              >
                {refundLoading ? 'Processing…' : 'Mark as Refunded'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Mark as Paid Confirmation Dialog */}
      <Dialog
        open={!!markPaidTarget}
        onClose={() => setMarkPaidTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        {markPaidTarget && (
          <>
            <DialogTitle>Mark Registration as Paid</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Are you sure you want to mark the registration for{' '}
                <strong>{markPaidTarget.firstName} {markPaidTarget.lastName}</strong> ({markPaidTarget.email}) as paid?
                This will confirm their spot and send them a confirmation email.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setMarkPaidTarget(null)}>
                Cancel
              </Button>
              <Button
                onClick={handleMarkPaid}
                color="success"
                variant="contained"
                disabled={markPaidLoading}
                startIcon={markPaidLoading ? <CircularProgress size={16} /> : <MarkPaidIcon />}
              >
                {markPaidLoading ? 'Processing…' : 'Mark as Paid'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        maxWidth="sm"
        fullWidth
      >
        {deleteTarget && (
          <>
            <DialogTitle>Delete Registration</DialogTitle>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                Are you sure you want to permanently delete the registration for{' '}
                <strong>{deleteTarget.firstName} {deleteTarget.lastName}</strong> ({deleteTarget.email})?
                This cannot be undone.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDeleteTarget(null)}>
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                color="error"
                variant="contained"
                disabled={deleteLoading}
                startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
              >
                {deleteLoading ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </AdminLayout>
  );
};

const DetailRow = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 1 }}>
    <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 160 }}>
      {label}:
    </Typography>
    <Typography variant="body2">{value || '—'}</Typography>
  </Box>
);

export default EventRegistrationDashboardPage;
